import { useCallback, useMemo, useRef, useState } from 'react';
import Popup, { PopupHandle } from '../components/popup';
import { TextOrAudioResponseQuestion } from '../components/questions/TextOrAudioResponseQuestion';
import { submitBugReport } from '../services/api';
import type { Question, ResponsePayload } from '../types';

interface FeedbackContext {
  question?: Question | null;
  response?: ResponsePayload | null;
}

export const useFeedbackPopup = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [question, setQuestion] = useState<Question | null>(null);
  const [response, setResponse] = useState<ResponsePayload | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const popupRef = useRef<PopupHandle | null>(null);

  const questionId = question?.id ?? null;

  const currentResponse = useMemo<Partial<ResponsePayload> | null>(() => {
    if (response?.question) {
      return response;
    }

    return questionId ? { question: questionId } : null;
  }, [questionId, response]);

  const handleClosePopup = useCallback(() => {
    setFeedbackText('');
    setAudioBlob(null);
    setIsSubmittingFeedback(false);
    setIsPopupOpen(false);
  }, []);

  const handleSubmitFeedback = useCallback(async () => {
    if (!feedbackText.trim()) {
      return;
    }

    setIsSubmittingFeedback(true);

    try {
      const submittedResponse = currentResponse ?? {};
      const metadata: Record<string, unknown> = {};

      if (typeof questionId === 'number') {
        metadata.question_id = questionId;
      }
      if (question?.key) {
        metadata.question_key = question.key;
      }
      if (submittedResponse && Object.keys(submittedResponse).length > 0) {
        metadata.submitted_response = submittedResponse;
      }

      await submitBugReport({
        feedback_text: feedbackText,
        metadata: Object.keys(metadata).length > 0 ? metadata : null,
      });

      popupRef.current?.close();
    } catch (err) {
      console.error('Error submitting bug report:', err);
    } finally {
      setIsSubmittingFeedback(false);
    }
  }, [currentResponse, feedbackText, question?.key, questionId]);

  const openFeedbackPopup = useCallback(({ question: nextQuestion = null, response: nextResponse = null }: FeedbackContext = {}) => {
    setQuestion(nextQuestion ?? null);
    setResponse(nextResponse ?? null);
    setIsPopupOpen(true);
  }, []);

  const setFeedbackContext = useCallback(({ question: nextQuestion, response: nextResponse }: FeedbackContext) => {
    if (nextQuestion !== undefined) {
      setQuestion(nextQuestion ?? null);
    }
    if (nextResponse !== undefined) {
      setResponse(nextResponse ?? null);
    }
  }, []);

  const feedbackPopup = isPopupOpen ? (
    <Popup
      ref={popupRef}
      onClose={handleClosePopup}
      title="Submit Feedback"
      header="PARDON OUR DUST"
      description="Please describe what you are experiencing and our team will do our best to resolve this!"
      buttonText="Submit"
      onClickButton={handleSubmitFeedback}
      isSubmitting={isSubmittingFeedback}
      isButtonDisabled={feedbackText.trim().length < 10}
    >
      <TextOrAudioResponseQuestion
        placeholder="Enter Response Here"
        inputTextValue={feedbackText}
        audioBlob={audioBlob}
        onTextChange={setFeedbackText}
        onAudioChange={setAudioBlob}
        isSubmitting={isSubmittingFeedback}
        minCharacters={10}
      />
    </Popup>
  ) : null;

  return {
    FeedbackPopup: feedbackPopup,
    isFeedbackPopupOpen: isPopupOpen,
    isSubmittingFeedback,
    openFeedbackPopup,
    closeFeedbackPopup: handleClosePopup,
    setFeedbackContext,
  };
};
